import Phaser from 'phaser';
import { EGGS_BY_ID } from '@/config/eggs.config';
import {
  HATCH_BULK_PRESET,
  bulkGridLayout,
  bulkPhasesTotalMs,
  rarityOrderIndex,
} from '@/config/hatchPresets.config';
import {
  variantScaleMult,
  applyVariantFx,
  destroyVariantFx,
  type VariantFxOwner,
  type VariantFxRefs,
} from '@/game/fx/variantFx';
import type { PetDef } from '@/domain/types/pet';
import type { Variant, ID } from '@/domain/types/common';

/**
 * HatchBulkDirector — orkiestrator bulk hatch cinematic (5.D.3c).
 *
 * Implementuje 5-fazową animację z `DESIGN_BULK_HATCH.md` §"Mockup animacji":
 *
 *   FAZA 1 — INTRO (~0.4 s):
 *     dim + stos jajek (piramida) wyłania się z dołu, glow ring, gold dust
 *
 *   FAZA 2 — CHARGE (~0.6 s):
 *     pulse'y stosu, shockwave ring narastający, subtle camera shake;
 *     HINT RZADKOŚCI: gold/rainbow pulse na 1 jajku jeśli batch ma
 *     legendary+ / huge — wizualny tease przed wybuchem
 *
 *   FAZA 3 — BURST (~0.3 s):
 *     full-screen white flash, rainbow confetti 50, shockwave ring leci na
 *     zewnątrz, camera punch
 *
 *   FAZA 4 — REVEAL GRID (~0.8 s base + N×90ms stagger):
 *     pety wjeżdżają w layout `cols × rows` per N (1×2 / 2×2 / 2×3 / 2×4 /
 *     2×5), SORTOWANE wg rarity (common najpierw, legendary/huge na końcu).
 *     Bounce skala 1.25 → 1.0, variant FX (gold shimmer / rainbow tint /
 *     huge aura) aplikowane przez `applyVariantFx`.
 *
 *   FAZA 5 — STAND-BY (do dismiss):
 *     grid stoi, huge'y mają gentle pulse, legendary/rainbow shimmer.
 *     React renderuje overlay z liczbą petów / nowych typów + dismiss button.
 *     `play()` resolveje po fazie 4 → React odpala showResult overlay,
 *     pety zostają widoczne w containerze do `destroy()`.
 *
 * **Cleanup:** ten sam wzorzec co `HatchDirector` — wszystkie tweeny/timery/
 * emittery/displayObjects w internal arrays. `destroy()` idempotentny.
 *
 * **Skip:** dostępny po `SKIP_AVAILABLE_AFTER_MS = 500`. Skip resolveje
 * `play()` natychmiast — fazy 1-3 pomijane, grid pojawia się od razu.
 */

export interface HatchBulkPetEntry {
  petDef: PetDef;
  variant: Variant;
  isHuge: boolean;
  power: number;
}

export interface HatchBulkPayload {
  pets: HatchBulkPetEntry[];
  eggDefId: ID;
}

export const BULK_SKIP_AVAILABLE_AFTER_MS = HATCH_BULK_PRESET.skipAvailableAfterMs;

/** "Bigness" rank wariantu rozmiarowego: titanic(2) > huge(1) > reszta(0). */
function variantBigRank(entry: HatchBulkPetEntry): number {
  if (entry.variant === 'titanic') return 2;
  if (entry.isHuge || entry.variant === 'huge') return 1;
  return 0;
}

/** Czy w batchu jest legendary+/huge/titanic — driver "gold shine" tease w fazie charge. */
function hasLegendaryOrHuge(pets: HatchBulkPetEntry[]): boolean {
  for (const p of pets) {
    if (variantBigRank(p) > 0) return true;
    const r = p.petDef.rarity;
    if (r === 'legendary' || r === 'mythic') return true;
  }
  return false;
}

export class HatchBulkDirector {
  private readonly tweens: Phaser.Tweens.Tween[] = [];
  private readonly timers: Phaser.Time.TimerEvent[] = [];
  private readonly emitters: Phaser.GameObjects.Particles.ParticleEmitter[] = [];
  private readonly displayObjects: Phaser.GameObjects.GameObject[] = [];
  /** Variant FX refs per index (sortowany wg rarity) — gold/rainbow/huge. */
  private readonly variantFxRefs: Array<VariantFxRefs | null> = [];
  /** Owners dla variant tween properties (rainbowHue, goldShimmerProgress) per pet. */
  private readonly variantOwners: VariantFxOwner[] = [];

  private completed = false;
  private skipRequested = false;
  private resolvePlay: (() => void) | null = null;
  private readonly startedAtMs: number;

  /** Pety posortowane wg rarity dla reveal phase (common → ... → huge). */
  private readonly sortedPets: HatchBulkPetEntry[];

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly payload: HatchBulkPayload,
  ) {
    this.startedAtMs = scene.time.now;
    // Sortuj stabilnie: rarity index asc, huge/titanic zawsze na końcu (titanic
    // za huge). Stable sort gwarantuje że pety tej samej rangi zachowują FIFO.
    this.sortedPets = [...payload.pets].sort((a, b) => {
      const ab = variantBigRank(a);
      const bb = variantBigRank(b);
      if (ab !== bb) return ab - bb;
      return rarityOrderIndex(a.petDef.rarity) - rarityOrderIndex(b.petDef.rarity);
    });
  }

  play(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.resolvePlay = resolve;

      const cam = this.scene.cameras.main;
      const centerX = cam.scrollX + cam.width / 2;
      const centerY = cam.scrollY + cam.height / 2;

      const container = this.scene.add.container(centerX, centerY);
      container.setDepth(1000);
      this.displayObjects.push(container);

      // ── Dim tło ──────────────────────────────────────────────────────────
      const dim = this.scene.add.rectangle(0, 0, cam.width, cam.height, 0x000000, 0);
      dim.setOrigin(0, 0);
      dim.setScrollFactor(0);
      dim.setDepth(999);
      this.displayObjects.push(dim);

      this.addTween({
        targets: dim,
        alpha: HATCH_BULK_PRESET.introDimAlpha,
        duration: HATCH_BULK_PRESET.introMs,
        ease: 'Quad.easeOut',
      });

      // ── FAZA 1: INTRO — stos jajek ─────────────────────────────────────
      const stack = this.spawnEggStack(container);

      // ── FAZA 2: CHARGE — pulse'y + shockwave ────────────────────────────
      this.addTimer(HATCH_BULK_PRESET.introMs, () => this.playChargePhase(container, stack));

      // ── FAZA 3: BURST — flash + confetti ────────────────────────────────
      const burstAt = HATCH_BULK_PRESET.introMs + HATCH_BULK_PRESET.chargeMs;
      this.addTimer(burstAt, () => this.playBurstPhase(container, stack, dim));

      // ── FAZA 4: REVEAL GRID — pety wjeżdżają ze stagger ─────────────────
      const revealAt = burstAt + HATCH_BULK_PRESET.burstMs;
      this.addTimer(revealAt, () => this.playRevealPhase(container));

      // ── End: resolve po skończeniu fazy 4. React przejmuje overlay. ──────
      this.addTimer(bulkPhasesTotalMs(this.sortedPets.length), () => this.complete());
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // FAZA 1: INTRO — stos jajek (piramida) wyłania się z dołu
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Stos jajek — piramida 3 rzędów. Wyjeżdża z dołu ekranu (slide up), każde
   * z miękkim glow ringiem. Zwraca container ze wszystkimi jajkami żeby fazy
   * 2-3 mogły go pulsować/usuwać.
   */
  private spawnEggStack(container: Phaser.GameObjects.Container): Phaser.GameObjects.Container {
    const stackContainer = this.scene.add.container(0, 0);
    container.add(stackContainer);

    const eggDef = EGGS_BY_ID[this.payload.eggDefId];
    const eggTextureKey = eggDef?.art.textureKey ?? 'egg_basic';
    const hasEggSprite = this.scene.textures.exists(eggTextureKey);

    // Piramida 3 rzędów: 1 / 2 / 3 (od góry). Total 6 jajek (wystarczy wizualnie
    // niezależnie od bulkSize 2-12 — to symboliczne, nie 1:1).
    const cam = this.scene.cameras.main;
    const minDim = Math.min(cam.width, cam.height);
    const eggSize = minDim * HATCH_BULK_PRESET.introEggScale;
    const eggSpacing = eggSize * 1.05;

    const rows = HATCH_BULK_PRESET.introEggStackRows;
    const eggs: Phaser.GameObjects.Image[] = [];

    for (let row = 0; row < rows; row++) {
      const eggsInRow = row + 1;   // 1, 2, 3
      const rowY = (rows - 1 - row) * (-eggSpacing * 0.85);   // wyżej = mniejsza Y
      const rowWidthHalf = ((eggsInRow - 1) * eggSpacing) / 2;
      for (let i = 0; i < eggsInRow; i++) {
        const x = -rowWidthHalf + i * eggSpacing;
        let egg: Phaser.GameObjects.Image;
        if (hasEggSprite) {
          egg = this.scene.add.image(x, rowY, eggTextureKey);
          const native = Math.max(egg.width, egg.height);
          egg.setScale((eggSize / native) * 0.95);
        } else {
          const fallback = this.scene.add.circle(x, rowY, eggSize / 2, 0xffd24a, 0.9);
          fallback.setStrokeStyle(3, 0x000000);
          egg = fallback as unknown as Phaser.GameObjects.Image;
        }
        egg.setAlpha(0);
        stackContainer.add(egg);
        eggs.push(egg);
      }
    }

    // Slide up + fade in — całość stosu wjeżdża z dołu ekranu.
    stackContainer.setY(minDim * 0.4);   // start poniżej środka
    this.addTween({
      targets: stackContainer,
      y: minDim * 0.05,                  // nieco poniżej środka (zostaje miejsce na górze)
      duration: HATCH_BULK_PRESET.introMs,
      ease: 'Back.easeOut',
    });
    for (let i = 0; i < eggs.length; i++) {
      this.addTween({
        targets: eggs[i],
        alpha: 1,
        duration: HATCH_BULK_PRESET.introMs * 0.7,
        delay: i * 25,                   // stagger fade-in
        ease: 'Quad.easeOut',
      });
    }

    // Gold dust particles — subtle ambient. Emitted z centrum stosu, spread
    // przez random angle. Bez emitZone (Phaser typing trudne) — wystarczająco
    // ładnie wygląda z natywnym dispersem.
    if (this.scene.textures.exists('fx-particle-yellow')) {
      const dust = this.scene.add.particles(0, 0, 'fx-particle-yellow', {
        speed: { min: 20, max: 50 },
        lifespan: 1200,
        scale: { start: 0.5, end: 0 },
        alpha: { start: 0.6, end: 0 },
        tint: 0xffd24a,
        frequency: 1000 / HATCH_BULK_PRESET.introDustParticleRate,
        angle: { min: 0, max: 360 },
        gravityY: -30,
      });
      dust.setDepth(1001);
      stackContainer.add(dust);
      this.emitters.push(dust);
    }

    return stackContainer;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // FAZA 2: CHARGE — pulse'y, shockwave, rarity tease
  // ─────────────────────────────────────────────────────────────────────────

  private playChargePhase(
    container: Phaser.GameObjects.Container,
    stack: Phaser.GameObjects.Container,
  ): void {
    if (this.completed) return;

    const pulseMs = HATCH_BULK_PRESET.chargeMs / HATCH_BULK_PRESET.chargePulses;
    const baseScale = stack.scale;

    // Pulse'y całego stosu.
    this.addTween({
      targets: stack,
      scale: { from: baseScale, to: baseScale * 1.15 },
      duration: pulseMs / 2,
      yoyo: true,
      repeat: HATCH_BULK_PRESET.chargePulses - 1,
      ease: 'Sine.easeInOut',
    });

    // Shockwave ring narastający przez całą fazę charge.
    const ring = this.scene.add.graphics();
    ring.setBlendMode(Phaser.BlendModes.ADD);
    stack.add(ring);

    const ringMaxR = HATCH_BULK_PRESET.chargeShockwaveRadius;
    const ringStart = this.scene.time.now;
    const ringDuration = HATCH_BULK_PRESET.chargeMs;
    const ringTimer = this.scene.time.addEvent({
      delay: 16,
      loop: true,
      callback: () => {
        if (this.completed) {
          ringTimer.remove();
          return;
        }
        const elapsed = this.scene.time.now - ringStart;
        const t = Math.min(1, elapsed / ringDuration);
        const r = ringMaxR * t;
        const alpha = (1 - t) * 0.7;
        ring.clear();
        ring.lineStyle(HATCH_BULK_PRESET.chargeShockwaveLineWidth, 0xfff0a0, alpha);
        ring.strokeCircle(0, 0, r);
        if (t >= 1) ringTimer.remove();
      },
    });
    this.timers.push(ringTimer);

    // Subtle camera shake przez container — bez efektu na dim.
    this.playContainerShake(container, HATCH_BULK_PRESET.chargeShakeIntensity, HATCH_BULK_PRESET.chargeMs);

    // Rarity tease — gold/rainbow pulse na 1 jajku jeśli batch ma legendary/huge.
    if (hasLegendaryOrHuge(this.sortedPets)) {
      const teaseEgg = stack.list.find((c) => c instanceof Phaser.GameObjects.Image) as
        | Phaser.GameObjects.Image
        | undefined;
      if (teaseEgg) {
        // Złoty tint cykliczny w drugiej połowie charge'a.
        this.addTimer(HATCH_BULK_PRESET.chargeMs * 0.4, () => {
          if (this.completed) return;
          teaseEgg.setTint(0xffd24a);
          this.addTween({
            targets: teaseEgg,
            scale: { from: teaseEgg.scale, to: teaseEgg.scale * 1.2 },
            duration: HATCH_BULK_PRESET.chargeMs * 0.3,
            yoyo: true,
            ease: 'Sine.easeInOut',
          });
        });
      }
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // FAZA 3: BURST — full-screen flash + confetti + shockwave outward
  // ─────────────────────────────────────────────────────────────────────────

  private playBurstPhase(
    container: Phaser.GameObjects.Container,
    stack: Phaser.GameObjects.Container,
    dim: Phaser.GameObjects.Rectangle,
  ): void {
    if (this.completed) return;

    const cam = this.scene.cameras.main;

    // Stos znika — quick scale up + fade.
    this.addTween({
      targets: stack,
      scale: stack.scale * 1.4,
      alpha: 0,
      duration: HATCH_BULK_PRESET.burstMs * 0.7,
      ease: 'Cubic.easeOut',
    });

    // Full-screen white flash — rectangle pełnoekranowy, NIE w containerze.
    // Niezależny od dim (który zostaje na fazę reveal/stand-by).
    const flash = this.scene.add.rectangle(0, 0, cam.width, cam.height, 0xffffff, 0);
    flash.setOrigin(0, 0);
    flash.setScrollFactor(0);
    flash.setDepth(1005);
    this.displayObjects.push(flash);
    this.addTween({
      targets: flash,
      alpha: HATCH_BULK_PRESET.burstFlashAlpha,
      duration: HATCH_BULK_PRESET.burstMs * 0.25,
      ease: 'Quad.easeOut',
      onComplete: () => {
        if (this.completed) return;
        this.addTween({
          targets: flash,
          alpha: 0,
          duration: HATCH_BULK_PRESET.burstMs * 0.55,
          ease: 'Quad.easeIn',
        });
      },
    });

    // Lekkie rozjaśnienie dim (delikatny shimmer per design doc faza 4-5).
    this.addTween({
      targets: dim,
      alpha: HATCH_BULK_PRESET.introDimAlpha * 0.5,
      duration: HATCH_BULK_PRESET.burstMs,
      ease: 'Quad.easeOut',
    });

    // Rainbow confetti — 50 particles z różnymi tintami, explosion z centrum stosu.
    this.spawnRainbowConfetti(container);

    // Shockwave ring outward.
    const burstRing = this.scene.add.graphics();
    burstRing.setBlendMode(Phaser.BlendModes.ADD);
    container.add(burstRing);
    const ringStart = this.scene.time.now;
    const ringDuration = HATCH_BULK_PRESET.burstMs + 200;
    const ringTimer = this.scene.time.addEvent({
      delay: 16,
      loop: true,
      callback: () => {
        if (this.completed) {
          ringTimer.remove();
          return;
        }
        const elapsed = this.scene.time.now - ringStart;
        const t = Math.min(1, elapsed / ringDuration);
        const r = HATCH_BULK_PRESET.burstShockwaveRadius * t;
        const alpha = (1 - t) * 0.85;
        burstRing.clear();
        burstRing.lineStyle(HATCH_BULK_PRESET.burstShockwaveLineWidth, 0xffffff, alpha);
        burstRing.strokeCircle(0, 0, r);
        if (t >= 1) ringTimer.remove();
      },
    });
    this.timers.push(ringTimer);

    // Container shake — camera punch.
    this.playContainerShake(
      container,
      HATCH_BULK_PRESET.burstShakeIntensity,
      HATCH_BULK_PRESET.burstShakeMs,
    );
  }

  private spawnRainbowConfetti(container: Phaser.GameObjects.Container): void {
    if (!this.scene.textures.exists('fx-particle-yellow')) return;

    // 5 emitterów z różnymi tintami → "rainbow" feel bez prawdziwego gradient
    // animation (Phaser particles nie wspiera per-particle dynamic tint poza
    // emit-time, więc 5 emitterów = 5 kolorów rozłożonych w batchu).
    const colors = [0xff5577, 0xffaa33, 0xffee44, 0x44dd88, 0x55aaff, 0xcc77ff];
    const perEmitter = Math.ceil(HATCH_BULK_PRESET.burstConfettiCount / colors.length);

    for (const color of colors) {
      const emitter = this.scene.add.particles(0, 0, 'fx-particle-yellow', {
        speed: { min: 150, max: 380 },
        lifespan: 900,
        scale: { start: 1.1, end: 0 },
        angle: { min: 0, max: 360 },
        rotate: { min: 0, max: 360 },
        tint: color,
        quantity: perEmitter,
        gravityY: 200,
        emitting: false,
      });
      emitter.setDepth(1002);
      container.add(emitter);
      emitter.explode(perEmitter, 0, 0);
      this.emitters.push(emitter);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // FAZA 4: REVEAL GRID — pety wjeżdżają ze stagger + variant FX
  // ─────────────────────────────────────────────────────────────────────────

  private playRevealPhase(container: Phaser.GameObjects.Container): void {
    if (this.completed) return;

    const cam = this.scene.cameras.main;
    const n = this.sortedPets.length;
    const layout = bulkGridLayout(n);
    const minDim = Math.min(cam.width, cam.height);
    const petDisplayPx = minDim * (layout.isBig ? HATCH_BULK_PRESET.petBigDisplayPct : HATCH_BULK_PRESET.petMidDisplayPct);

    // Grid bounds: szerokość 80% viewport, wysokość 60% viewport, wycentrowane.
    const gridW = cam.width * 0.85;
    const gridH = cam.height * 0.6;
    const cellW = gridW / layout.cols;
    const cellH = gridH / layout.rows;

    // Top-left pierwszej komórki względem środka containera (= środek ekranu).
    // Y offset: trochę w dół żeby dać miejsce na "Zdobyłeś N petów" overlay z góry.
    const yOffset = 30;
    const startX = -gridW / 2 + cellW / 2;
    const startY = -gridH / 2 + cellH / 2 + yOffset;

    for (let i = 0; i < n; i++) {
      const pet = this.sortedPets[i];
      const row = Math.floor(i / layout.cols);
      const col = i % layout.cols;

      // Centruj ostatni (potencjalnie niepełny) rząd: jeśli to ostatni rząd
      // i mniej petów niż cols, wsuń kolumnę do środka.
      const isLastRow = row === layout.rows - 1;
      const itemsInLastRow = n - row * layout.cols;
      const colShift = isLastRow && itemsInLastRow < layout.cols
        ? (layout.cols - itemsInLastRow) / 2
        : 0;

      const x = startX + (col + colShift) * cellW;
      const y = startY + row * cellH;

      const delay = i * HATCH_BULK_PRESET.revealStaggerMs;
      this.addTimer(delay, () => this.revealOnePet(container, pet, x, y, petDisplayPx, i));
    }
  }

  private revealOnePet(
    container: Phaser.GameObjects.Container,
    entry: HatchBulkPetEntry,
    x: number,
    y: number,
    displayPx: number,
    index: number,
  ): void {
    if (this.completed) return;

    const petTextureKey = entry.petDef.art.textureKey;
    if (!this.scene.textures.exists(petTextureKey)) {
      console.warn(`[HatchBulkDirector] Brak tekstury peta '${petTextureKey}', pomijam reveal`);
      return;
    }

    const pet = this.scene.add.image(x, y, petTextureKey);
    pet.setScale(0);
    container.add(pet);

    const native = Math.max(pet.width, pet.height);
    const sizeMult = variantScaleMult(entry.variant);
    const targetScale = (displayPx / Math.max(1, native)) * sizeMult;

    // Bounce: 0 → targetScale × 1.25 → targetScale.
    const peak = targetScale * HATCH_BULK_PRESET.bouncePeakMult;
    const ratio = variantBigRank(entry) > 0 ? 0.7 : 0.6;
    this.addTween({
      targets: pet,
      scale: peak,
      duration: HATCH_BULK_PRESET.revealBaseMs * ratio,
      ease: 'Back.easeOut',
      onComplete: () => {
        if (this.completed) return;
        this.addTween({
          targets: pet,
          scale: targetScale,
          duration: HATCH_BULK_PRESET.revealBaseMs * (1 - ratio),
          ease: 'Quad.easeOut',
        });
      },
    });

    // Variant FX — gold shimmer / rainbow tint / huge aura (DRY z Pet.ts).
    const owner: VariantFxOwner = { goldShimmerProgress: 0, rainbowHue: 0 };
    this.variantOwners.push(owner);
    const auraRadius = displayPx * 0.55 * sizeMult;
    const refs = applyVariantFx(this.scene, pet, entry.variant, owner, { x, y }, auraRadius);
    if (refs.aura) {
      refs.aura.setPosition(x, y);
      container.add(refs.aura);
      container.moveDown(refs.aura);
    }
    this.variantFxRefs[index] = refs;

    // Per-rarity sparkle burst przy reveal (epic+).
    this.spawnRevealSparkle(container, entry, x, y, displayPx);
  }

  /** Mini-FX dla rare+ przy reveal: kolor + ilość per rarity. */
  private spawnRevealSparkle(
    container: Phaser.GameObjects.Container,
    entry: HatchBulkPetEntry,
    x: number,
    y: number,
    displayPx: number,
  ): void {
    const r = entry.petDef.rarity;
    if (r === 'common' || r === 'uncommon') return;

    const sparkleConfig: { color: number; count: number; texKey: string } =
      entry.variant === 'titanic'
        ? { color: 0xcbb0ff, count: 30, texKey: 'fx-particle-white' }
        : entry.isHuge || entry.variant === 'huge'
        ? { color: 0xfff7ad, count: 24, texKey: 'fx-particle-yellow' }
        : r === 'mythic'
          ? { color: 0xf87171, count: 22, texKey: 'fx-particle-yellow' }
          : r === 'legendary'
            ? { color: 0xfacc15, count: 20, texKey: 'fx-particle-yellow' }
            : r === 'epic'
              ? { color: 0xc084fc, count: 14, texKey: 'fx-particle-cyan' }
              : { color: 0x60a5fa, count: 8, texKey: 'fx-particle-cyan' };   // rare

    if (!this.scene.textures.exists(sparkleConfig.texKey)) return;

    const emitter = this.scene.add.particles(x, y, sparkleConfig.texKey, {
      speed: { min: 60, max: 160 },
      lifespan: 600,
      scale: { start: 0.8, end: 0 },
      angle: { min: 0, max: 360 },
      tint: sparkleConfig.color,
      quantity: sparkleConfig.count,
      emitting: false,
    });
    emitter.setDepth(1003);
    container.add(emitter);
    emitter.explode(sparkleConfig.count, x, y);
    this.emitters.push(emitter);

    // Gold glow ring dla epic+ / huge / titanic — zatrzymujący się okrąg wokół peta.
    if (r === 'epic' || r === 'legendary' || r === 'mythic' || variantBigRank(entry) > 0) {
      const ring = this.scene.add.graphics();
      ring.setBlendMode(Phaser.BlendModes.ADD);
      container.add(ring);
      const ringStart = this.scene.time.now;
      const ringDuration = 500;
      const maxR = displayPx * 0.65;
      const ringTimer = this.scene.time.addEvent({
        delay: 16,
        loop: true,
        callback: () => {
          if (this.completed) {
            ringTimer.remove();
            return;
          }
          const elapsed = this.scene.time.now - ringStart;
          const t = Math.min(1, elapsed / ringDuration);
          const r2 = maxR * t;
          const alpha = (1 - t) * 0.7;
          ring.clear();
          ring.lineStyle(4, sparkleConfig.color, alpha);
          ring.strokeCircle(x, y, r2);
          if (t >= 1) ringTimer.remove();
        },
      });
      this.timers.push(ringTimer);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Container shake (DRY: zsynchronizowane z HatchDirector.playContainerShake)
  // ─────────────────────────────────────────────────────────────────────────

  private playContainerShake(
    container: Phaser.GameObjects.Container,
    intensityRel: number,
    durationMs: number,
  ): void {
    const cam = this.scene.cameras.main;
    const maxOffsetPx = Math.max(cam.width, cam.height) * intensityRel;
    const originalX = container.x;
    const originalY = container.y;
    const startMs = this.scene.time.now;

    const shakeTimer = this.scene.time.addEvent({
      delay: 30,
      loop: true,
      callback: () => {
        if (this.completed) {
          container.setPosition(originalX, originalY);
          shakeTimer.remove();
          return;
        }
        const elapsed = this.scene.time.now - startMs;
        if (elapsed >= durationMs) {
          container.setPosition(originalX, originalY);
          shakeTimer.remove();
          return;
        }
        const decay = 1 - (elapsed / durationMs);
        const offsetX = (Math.random() - 0.5) * 2 * maxOffsetPx * decay;
        const offsetY = (Math.random() - 0.5) * 2 * maxOffsetPx * decay;
        container.setPosition(originalX + offsetX, originalY + offsetY);
      },
    });
    this.timers.push(shakeTimer);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Skip / complete / destroy
  // ─────────────────────────────────────────────────────────────────────────

  skip(): void {
    if (this.completed || this.skipRequested) return;
    const elapsed = this.scene.time.now - this.startedAtMs;
    if (elapsed < BULK_SKIP_AVAILABLE_AFTER_MS) return;
    this.skipRequested = true;
    this.complete();
  }

  private complete(): void {
    if (this.completed) return;
    this.completed = true;
    this.destroy();
    this.resolvePlay?.();
    this.resolvePlay = null;
  }

  destroy(): void {
    for (const tween of this.tweens) {
      tween.remove();
    }
    this.tweens.length = 0;

    for (const timer of this.timers) {
      timer.remove();
    }
    this.timers.length = 0;

    for (const emitter of this.emitters) {
      emitter.destroy();
    }
    this.emitters.length = 0;

    // Variant FX cleanup PRZED container.destroy(true) — tweens muszą się
    // odpiąć od sprite'ów zanim ich destroy() zniknie.
    for (const refs of this.variantFxRefs) {
      if (refs) destroyVariantFx(refs);
    }
    this.variantFxRefs.length = 0;
    this.variantOwners.length = 0;

    for (const obj of this.displayObjects) {
      if ('destroy' in obj && typeof obj.destroy === 'function') {
        obj.destroy(true);
      }
    }
    this.displayObjects.length = 0;
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  private addTween(config: Phaser.Types.Tweens.TweenBuilderConfig): Phaser.Tweens.Tween {
    const tween = this.scene.tweens.add(config);
    this.tweens.push(tween);
    return tween;
  }

  private addTimer(delayMs: number, callback: () => void): Phaser.Time.TimerEvent {
    const timer = this.scene.time.delayedCall(delayMs, callback);
    this.timers.push(timer);
    return timer;
  }
}

